import React, { useState, useEffect, useCallback } from 'react';
import { Page, Layout, Card, DataTable, Badge, Button, Modal } from '@shopify/polaris';
import { reviewService } from '../services/reviewService';
import LoadingState from './shared/LoadingState';
import PageError from './shared/PageError';
import ReviewDetail from './shared/ReviewDetail';

const statusTone = { pending: 'warning', approved: 'success', rejected: 'critical' };

const periodLabel = (review) =>
  new Date(review.year, review.month - 1, 1).toLocaleString('en-US', { month: 'long', year: 'numeric' });

// Manager-facing: only reviews assigned to the logged-in manager.
const ReviewTimes = () => {
  const [reviews, setReviews] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [activeReviewId, setActiveReviewId] = useState(null);

  const load = useCallback(async () => {
    try {
      setLoading(true);
      setError('');
      const data = await reviewService.getMine();
      setReviews(data.reviews || []);
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to load reviews');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  if (loading) {
    return (
      <Page title="Review Times">
        <LoadingState />
      </Page>
    );
  }

  if (error) {
    return (
      <Page title="Review Times" primaryAction={{ content: 'Retry', onAction: load }}>
        <PageError>{error}</PageError>
      </Page>
    );
  }

  const rows = reviews.map((review) => [
    periodLabel(review),
    <Badge tone={statusTone[review.status]}>{review.status}</Badge>,
    <Button size="slim" onClick={() => setActiveReviewId(review._id)}>
      {review.status === 'pending' ? 'Review' : 'View'}
    </Button>
  ]);

  return (
    <Page title="Review Times" subtitle="Monthly hours for the people who report to you" primaryAction={{ content: 'Refresh', onAction: load }}>
      <Layout>
        <Layout.Section>
          <Card>
            {reviews.length === 0 ? (
              <p style={{ textAlign: 'center', padding: '2rem' }}>No reviews assigned to you yet.</p>
            ) : (
              <DataTable columnContentTypes={['text', 'text', 'text']} headings={['Period', 'Status', 'Actions']} rows={rows} />
            )}
          </Card>
        </Layout.Section>
      </Layout>

      <Modal open={!!activeReviewId} onClose={() => setActiveReviewId(null)} title="Monthly Time Review" large>
        <Modal.Section>
          {activeReviewId && (
            <ReviewDetail
              reviewId={activeReviewId}
              onDecided={() => {
                load();
              }}
            />
          )}
        </Modal.Section>
      </Modal>
    </Page>
  );
};

export default ReviewTimes;
